import PrivateImage from './PrivateImage';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import remarkBreaks from 'remark-breaks';
import { Edit3 } from 'lucide-react';
import { processStreamingContent } from '../../utils/artifactParser';
import { preprocessMath } from '../../utils/mathUtils';
import { isImageUrl } from '../../utils/imageUtils';
import ArtifactCard from '../ArtifactCard';
import MessageWithInlineArtifacts from '../MessageWithInlineArtifacts';
import StreamingMessageRenderer from '../StreamingMessageRenderer';
import ReasoningSection from './ReasoningSection';
import MessageActions from './MessageActions';
import MessageCitations from './MessageCitations';
import MessageAttachment from './MessageAttachment';
import { createMarkdownComponents } from './MarkdownComponents';
import { useMessageSending } from '../../hooks/chat/useMessageSending';
import { useModels } from '../../contexts/ModelContext';
import ImageGenerationIndicator from './ImageGenerationIndicator';
import WebSearchTrace from './WebSearchTrace';

const MAX_IMAGE_SIZE = 300;
const INLINE_EDIT_UI_ENABLED = false;
const ASSISTANT_TEXT_CLASS = "chat-text markdown-content leading-relaxed text-base text-black dark:text-gray-200 font-medium";
const MARKDOWN_IMAGE_REGEX = /!\[[^\]]*\]\([^)]+\)/g;
const STREAMING_REMARK_PLUGINS = [remarkGfm, remarkMath, remarkBreaks];
const STREAMING_REHYPE_PLUGINS = [rehypeRaw, rehypeKatex];

const CompletedStreamingMarkdown = React.memo(({
  text,
  markdownComponents,
  className,
}) => (
  <ReactMarkdown
    className={className}
    remarkPlugins={STREAMING_REMARK_PLUGINS}
    rehypePlugins={STREAMING_REHYPE_PLUGINS}
    components={markdownComponents}
  >
    {preprocessMath(text)}
  </ReactMarkdown>
));

const StreamingText = ({ text, markdownComponents, className }) => {
  const lastParagraphBreak = text.lastIndexOf('\n\n');
  const completedText = lastParagraphBreak >= 0
    ? text.slice(0, lastParagraphBreak + 2)
    : '';
  const liveTail = lastParagraphBreak >= 0
    ? text.slice(lastParagraphBreak + 2)
    : text;

  return (
    <div className={className}>
      {completedText ? (
        <CompletedStreamingMarkdown
          text={completedText}
          markdownComponents={markdownComponents}
          className={className}
        />
      ) : null}
      {liveTail ? <span className="whitespace-pre-wrap">{liveTail}</span> : null}
    </div>
  );
};

const InlineMessageEditor = ({
  message,
  editingContext,
  selectedChat,
  selectedModel,
  onMessageSent,
  messages,
  updateChatTitle,
  hasSubscription,
  messageLimit,
  onCancelEdit,
  applyEditAndTruncate,
}) => {
  const [text, setText] = useState(editingContext?.text || message.text || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef(null);
  const { getModelByModelString } = useModels();
  const modelDetails = getModelByModelString(selectedModel);
  const selectedModelName = modelDetails?.name || selectedModel;

  useEffect(() => {
    setText(editingContext?.text || message.text || '');
    if (textareaRef.current) {
      textareaRef.current.focus();
      const valueLength = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(valueLength, valueLength);
    }
  }, [editingContext?.messageId, editingContext?.text, message.text]);

  const {
    handleSendEditedMessage,
    error,
    clearError,
  } = useMessageSending({
    selectedChat,
    selectedModel,
    selectedModelName,
    onMessageSent,
    messages,
    updateChatTitle,
    hasSubscription,
    messageLimit,
    input: text,
    setInput: setText,
    setInputText: () => {},
    attachments: [],
    clearFileState: () => {},
    isThinking: false,
    isDeepResearch: false,
    setIsDeepResearch: () => {},
    reasoningLevel: 'Auto',
    editingContext,
    onEditComplete: onCancelEdit,
    applyEditAndTruncate
  });

  const handleSubmit = async () => {
    if (isSubmitting || !text.trim()) return;
    setIsSubmitting(true);
    try {
      await handleSendEditedMessage(text);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleChange = (e) => {
    if (error) clearError();
    setText(e.target.value);
  };

  return (
    <div className="flex flex-col space-y-3 w-full">
      <textarea
        ref={textareaRef}
        className="w-full text-base leading-relaxed text-gray-900 dark:text-white resize-none rounded-2xl bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 px-4 py-3"
        rows={Math.min(8, Math.max(3, Math.ceil((text.length || 1) / 60)))}
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder="Edit your message"
        disabled={isSubmitting}
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(155,155,155,0.5) transparent',
        }}
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Messages after this will be replaced.
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancelEdit}
            disabled={isSubmitting}
            className="h-9 px-3 rounded-full text-sm bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !text.trim()}
            className="h-9 px-4 rounded-full text-sm bg-black text-white dark:bg-white dark:text-black disabled:opacity-50"
          >
            Send
          </button>
        </div>
      </div>
      {error && (
        <p className="text-sm text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};

const MessageBubble = ({
  message,
  isNewMessage,
  hoveredMessageId,
  setHoveredMessageId,
  copyStatus,
  isSpeaking,
  speakingMessageId,
  showReasoningMap,
  reasoningTimers,
  onSpeak,
  onCopy,
  onArtifactOpen,
  onImageClick,
  onToggleReasoning,
  onEditMessage,
  editingMessage,
  onCancelEdit,
  selectedChat,
  selectedModel,
  onMessageSent,
  updateChatTitle,
  hasSubscription,
  messageLimit,
  applyEditAndTruncate,
  chatMessages
}) => {
  const isEditing = editingMessage?.messageId === message.id;
  const markdownComponents = useMemo(
    () => createMarkdownComponents(onImageClick),
    [onImageClick]
  );

  const renderMessageContent = useCallback((message) => {
    const thinkingSeconds = reasoningTimers[message.id]?.elapsed || 0;
    const isLiveToolActivity = Boolean(
      message.streaming ||
      message.isWebSearching ||
      message.isImageGenerating
    );
    const assistantImageAttachment = Array.isArray(message.attachments)
      ? message.attachments.find(
          (attachment) =>
            attachment?.type === 'image' &&
            (attachment.url || attachment.previewUrl)
        )
      : null;
    const assistantImageUrl =
      assistantImageAttachment?.url || assistantImageAttachment?.previewUrl || '';
    const textWithoutMarkdownImage = (message.text || '')
      .replace(MARKDOWN_IMAGE_REGEX, '')
      .trim();
    const visibleAssistantText = message.text || '';
    const visibleAssistantMessage = visibleAssistantText
      ? { ...message, text: visibleAssistantText }
      : message;
    
    return message.role === 'assistant' ? (
      isLiveToolActivity && message.toolError && message.toolName === 'image_generation' ? (
        <div className="max-w-[360px] rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
          <p className="font-semibold">Image generation failed</p>
          <p className="mt-1">{message.text || 'The image tool returned an error. Please try again.'}</p>
        </div>
      ) : isLiveToolActivity && (message.isImageGenerating || message.imageGenerationStatus === 'in_progress') ? (
        <ImageGenerationIndicator />
      ) : assistantImageUrl ? (
        <>
          <PrivateImage
            src={assistantImageUrl}
            alt={assistantImageAttachment?.name || 'AI generated'}
            className="max-w-full h-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
            style={{
              maxWidth: `${MAX_IMAGE_SIZE}px`,
              maxHeight: `${MAX_IMAGE_SIZE}px`,
              objectFit: 'contain',
            }}
            onClick={(e) =>
              onImageClick(
                e.currentTarget.src,
                assistantImageAttachment?.name || 'AI generated',
                e.target
              )
            }
            title="Click to expand"
          />

          {textWithoutMarkdownImage ? (
            <MessageWithInlineArtifacts
              messageText={textWithoutMarkdownImage}
              onArtifactOpen={onArtifactOpen}
              markdownComponents={markdownComponents}
              className={ASSISTANT_TEXT_CLASS}
            />
          ) : null}

          <MessageActions
            message={message}
            isVisible={hoveredMessageId === message.id}
            isSpeaking={isSpeaking}
            speakingMessageId={speakingMessageId}
            copyStatus={copyStatus}
            onSpeak={onSpeak}
            onCopy={onCopy}
          />
        </>
      ) : (
      isImageUrl(message.text) ? (
        <PrivateImage
          src={message.text}
          alt="AI generated"
          className="max-w-full h-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
          style={{
            maxWidth: `${MAX_IMAGE_SIZE}px`,
            maxHeight: `${MAX_IMAGE_SIZE}px`,
            objectFit: 'contain',
          }}
          onClick={(e) => onImageClick(e.currentTarget.src, 'AI generated', e.target)}
          title="Click to expand"
        />
      ) : (
        <>
          <ReasoningSection
            message={message}
            showReasoning={showReasoningMap[message.id]}
            thinkingSeconds={thinkingSeconds}
            onToggleReasoning={onToggleReasoning}
          />

          {isLiveToolActivity ? (
            <WebSearchTrace
              calls={message.webSearchCalls}
              status={message.webSearchStatus}
            />
          ) : null}

          {/* Render assistant's main text with inline artifact processing */}
          {visibleAssistantText ? (() => {
            // For streaming messages, use inline rendering to preserve text order
            if (message.streaming) {
              if (!/<artifact\s/i.test(visibleAssistantText)) {
                return (
                  <StreamingText
                    text={visibleAssistantText}
                    markdownComponents={markdownComponents}
                    className={ASSISTANT_TEXT_CLASS}
                  />
                );
              }
              const { hasArtifact, currentArtifact, cleanText, allArtifacts, hasInlinePlaceholders } = processStreamingContent(visibleAssistantText);
              
              // Use inline rendering if we have placeholders, otherwise fall back to old approach
              if (hasInlinePlaceholders && allArtifacts) {
                return (
                  <StreamingMessageRenderer
                    cleanText={cleanText}
                    artifacts={allArtifacts}
                    currentArtifact={currentArtifact}
                    onArtifactOpen={onArtifactOpen}
                    markdownComponents={markdownComponents}
                    className={ASSISTANT_TEXT_CLASS}
                  />
                );
              }
              
              // Fallback for incomplete artifacts or no inline placeholders
              return (
                <>
                  <ReactMarkdown
                    className={ASSISTANT_TEXT_CLASS}
                    remarkPlugins={STREAMING_REMARK_PLUGINS}
                    rehypePlugins={STREAMING_REHYPE_PLUGINS}
                    components={markdownComponents}
                  >
                    {preprocessMath(cleanText)}
                  </ReactMarkdown>
                  {/* Show artifact card for streaming artifacts */}
                  {hasArtifact && currentArtifact && onArtifactOpen && (
                    <ArtifactCard
                      artifact={currentArtifact}
                      onClick={() => onArtifactOpen(currentArtifact)}
                    />
                  )}
                </>
              );
            }

            // For completed messages, render artifacts inline
            return (
              <MessageWithInlineArtifacts
                messageText={visibleAssistantText}
                onArtifactOpen={onArtifactOpen}
                markdownComponents={markdownComponents}
                className={ASSISTANT_TEXT_CLASS}
              />
            );
          })() : null}

          {visibleAssistantText ? (
            <MessageActions
              message={visibleAssistantMessage}
              isVisible={hoveredMessageId === message.id}
              isSpeaking={isSpeaking}
              speakingMessageId={speakingMessageId}
              copyStatus={copyStatus}
              onSpeak={onSpeak}
              onCopy={onCopy}
            />
          ) : null}
        </>
      )
      )
    ) : (
      // User role
      <div>{message.text}</div>
    );
  }, [hoveredMessageId, copyStatus, isSpeaking, speakingMessageId, showReasoningMap, reasoningTimers, onSpeak, onCopy, onArtifactOpen, onImageClick, onToggleReasoning, markdownComponents]);

  return (
    <div
      data-message-id={message.id}
      data-message-role={message.role}
      className={`flex flex-col ${
        message.role === 'user' ? 'items-end' : 'items-start'
      } ${isNewMessage ? 'animate-message-in' : ''}`}
      onMouseEnter={() => setHoveredMessageId(message.id)}
      onMouseLeave={() => setHoveredMessageId(null)}
    >
      {/* Render attachments above the message bubble */}
      <MessageAttachment
        message={message}
        onImageClick={onImageClick}
      />
      
      <div
        className={`inline-block px-6 py-2 rounded-3xl ${
          message.role === 'user'
            ? 'user-bubble theme-chat-bubble-user theme-chat-bubble-user-text max-w-[75%]'
            : 'text-black dark:text-white max-w-[85%]'
        } break-words`}
      >
        {isEditing && message.role === 'user' ? (
          <InlineMessageEditor
            message={message}
            editingContext={editingMessage}
            selectedChat={selectedChat}
            selectedModel={selectedModel}
            onMessageSent={onMessageSent}
            messages={chatMessages}
            updateChatTitle={updateChatTitle}
            hasSubscription={hasSubscription}
            messageLimit={messageLimit}
            onCancelEdit={onCancelEdit}
            applyEditAndTruncate={applyEditAndTruncate}
          />
        ) : (
          renderMessageContent(message)
        )}
      </div>
      
      <MessageCitations citations={message.citations} />
      
      {/* User actions under bubble (edit) */}
      {INLINE_EDIT_UI_ENABLED && message.role === 'user' && onEditMessage && !isEditing && (
        <div
          className={`flex space-x-1 mt-2 transition-opacity duration-200 ${
            hoveredMessageId === message.id ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <button
            onClick={() => onEditMessage(message)}
            className="p-1.5 rounded-full text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            title="Edit message"
          >
            <Edit3 size={16} />
          </button>
        </div>
      )}
      
      {message.pending && (
        <span className="text-xs italic text-gray-500 dark:text-gray-400 mt-1 mr-1 animate-pulse">
          Sending...
        </span>
      )}
      {message.error && (
        <span className="text-xs text-red-500 mt-1 mr-1">
          Error sending message
        </span>
      )}
    </div>
  );
};

const areMessageBubblePropsEqual = (previous, next) => {
  if (previous.message !== next.message) return false;
  if (previous.isNewMessage !== next.isNewMessage) return false;
  if ((previous.hoveredMessageId === previous.message.id) !==
      (next.hoveredMessageId === next.message.id)) return false;
  if (previous.copyStatus?.[previous.message.text] !==
      next.copyStatus?.[next.message.text]) return false;
  const previousIsSpeaking = previous.isSpeaking &&
    previous.speakingMessageId === previous.message.id;
  const nextIsSpeaking = next.isSpeaking &&
    next.speakingMessageId === next.message.id;
  if (previousIsSpeaking !== nextIsSpeaking) return false;
  if (previous.showReasoningMap?.[previous.message.id] !==
      next.showReasoningMap?.[next.message.id]) return false;
  if (previous.reasoningTimers?.[previous.message.id] !==
      next.reasoningTimers?.[next.message.id]) return false;
  const previousIsEditing = previous.editingMessage?.messageId === previous.message.id;
  const nextIsEditing = next.editingMessage?.messageId === next.message.id;
  if (previousIsEditing !== nextIsEditing) return false;
  if (previous.onSpeak !== next.onSpeak || previous.onCopy !== next.onCopy) return false;
  if (previous.onArtifactOpen !== next.onArtifactOpen || previous.onImageClick !== next.onImageClick) return false;
  if (previous.onToggleReasoning !== next.onToggleReasoning || previous.onEditMessage !== next.onEditMessage) return false;
  if (previous.onCancelEdit !== next.onCancelEdit || previous.applyEditAndTruncate !== next.applyEditAndTruncate) return false;
  if (previousIsEditing) {
    if (previous.selectedChat !== next.selectedChat) return false;
    if (previous.selectedModel !== next.selectedModel) return false;
    if (previous.hasSubscription !== next.hasSubscription) return false;
    if (previous.messageLimit !== next.messageLimit) return false;
    if (previous.editingMessage !== next.editingMessage) return false;
    if (previous.chatMessages !== next.chatMessages) return false;
    if (previous.onMessageSent !== next.onMessageSent) return false;
    if (previous.updateChatTitle !== next.updateChatTitle) return false;
  }
  return true;
};

export default React.memo(MessageBubble, areMessageBubblePropsEqual);
